<template>
  <main class="payment-checkout-page">
    <header class="checkout-hero">
      <span class="checkout-hero__orb checkout-hero__orb--one"></span>
      <span class="checkout-hero__orb checkout-hero__orb--two"></span>
      <div class="checkout-hero__content">
        <div class="checkout-hero__icon"><i class="fas fa-wallet"></i></div>
        <div>
          <span class="checkout-eyebrow checkout-eyebrow--light">Langkah 2 dari 3</span>
          <h1>Pilih metode pembayaran</h1>
          <p>Selesaikan langganan klinik Anda dengan proses yang ringkas dan aman.</p>
        </div>
      </div>
      <div class="checkout-security">
        <span><i class="fas fa-shield-alt"></i></span>
        <div><strong>Pembayaran terlindungi</strong><small>Data diproses melalui koneksi aman</small></div>
      </div>
    </header>

    <nav class="checkout-steps" aria-label="Tahapan pembayaran">
      <div class="checkout-step checkout-step--complete">
        <span><i class="fas fa-check"></i></span><div><small>Langkah 1</small><strong>Pilih paket</strong></div>
      </div>
      <i class="fas fa-chevron-right checkout-steps__arrow"></i>
      <div class="checkout-step checkout-step--active">
        <span>2</span><div><small>Langkah 2</small><strong>Pembayaran</strong></div>
      </div>
      <i class="fas fa-chevron-right checkout-steps__arrow"></i>
      <div class="checkout-step">
        <span>3</span><div><small>Langkah 3</small><strong>Konfirmasi</strong></div>
      </div>
    </nav>

    <div class="checkout-layout">
      <section class="checkout-methods-card" aria-labelledby="payment-method-title">
        <div class="checkout-section-heading">
          <div>
            <span class="checkout-eyebrow">Metode tersedia</span>
            <h2 id="payment-method-title">Pilih cara pembayaran</h2>
            <p>Pilih satu metode yang paling nyaman untuk Anda.</p>
          </div>
          <span v-if="!isLoading" class="checkout-count">{{ methods.length }} pilihan</span>
        </div>

        <div v-if="isLoading" class="checkout-method-grid" aria-live="polite">
          <div v-for="index in 6" :key="index" class="method-skeleton">
            <span></span><i></i><i></i>
          </div>
        </div>

        <div v-else-if="loadError" class="checkout-state checkout-state--error" role="alert">
          <span><i class="fas fa-exclamation-circle"></i></span>
          <div><strong>Metode pembayaran belum dapat dimuat</strong><p>{{ loadError }}</p></div>
          <button type="button" @click="getMethods">Coba lagi</button>
        </div>

        <div v-else-if="methods.length === 0" class="checkout-state">
          <span><i class="fas fa-credit-card"></i></span>
          <div><strong>Belum ada metode pembayaran</strong><p>Silakan coba beberapa saat lagi.</p></div>
        </div>

        <div v-else class="checkout-method-grid">
          <button
            v-for="method in methods"
            :key="method.code"
            type="button"
            class="payment-method"
            :class="{ 'payment-method--selected': selectedMethodCode === method.code }"
            :aria-pressed="selectedMethodCode === method.code ? 'true' : 'false'"
            @click="setPaymentMethod(method)"
          >
            <span v-if="selectedMethodCode === method.code" class="payment-method__check">
              <i class="fas fa-check"></i>
            </span>
            <span class="payment-method__logo">
              <img :src="method.url_image" :alt="method.payment_method_name" />
            </span>
            <span class="payment-method__content">
              <strong>{{ method.payment_method_name }}</strong>
              <small>{{ categoryLabel(method.category) }}</small>
            </span>
          </button>
        </div>

        <div class="checkout-assurance">
          <div><span><i class="fas fa-lock"></i></span><p><strong>Transaksi aman</strong><small>Informasi pembayaran tidak disimpan di perangkat ini.</small></p></div>
          <div><span><i class="fas fa-headset"></i></span><p><strong>Butuh bantuan?</strong><small>Tim Medhop siap membantu kendala pembayaran.</small></p></div>
        </div>
      </section>

      <aside class="checkout-summary">
        <div class="checkout-summary__header">
          <div><span class="checkout-eyebrow">Ringkasan pesanan</span><h2>Detail pembayaran</h2></div>
          <span class="checkout-summary__receipt"><i class="fas fa-receipt"></i></span>
        </div>

        <div class="checkout-package">
          <span class="checkout-package__icon"><i class="fas fa-box-open"></i></span>
          <div><small>Paket berlangganan</small><strong>{{ packageName }}</strong><span>{{ duration }} bulan akses</span></div>
        </div>

        <dl class="checkout-breakdown">
          <div><dt>Harga paket</dt><dd>{{ duration }} × {{ formatCurrency(unitPrice) }}</dd></div>
          <div><dt>Subtotal</dt><dd>{{ formatCurrency(subtotal) }}</dd></div>
          <div><dt>PPN 11%</dt><dd>{{ formatCurrency(taxAmount) }}</dd></div>
          <div><dt>Biaya layanan</dt><dd class="checkout-free">Gratis</dd></div>
        </dl>

        <div class="checkout-total">
          <span>Total pembayaran</span>
          <strong>{{ formatCurrency(totalAmount) }}</strong>
          <small>Sudah termasuk pajak</small>
        </div>

        <div class="checkout-selected-method" :class="{ 'checkout-selected-method--empty': !selectedMethodCode }">
          <span class="checkout-selected-method__logo">
            <img v-if="payment.url_image" :src="payment.url_image" alt="" />
            <i v-else class="far fa-credit-card"></i>
          </span>
          <div><small>Metode pembayaran</small><strong>{{ payment.payment_method_name || "Belum dipilih" }}</strong></div>
          <i v-if="selectedMethodCode" class="fas fa-check-circle"></i>
        </div>

        <p v-if="submitError" class="checkout-submit-error" role="alert">
          <i class="fas fa-exclamation-circle"></i> {{ submitError }}
        </p>

        <button
          type="button"
          class="checkout-pay-button"
          :disabled="!canCheckout"
          @click="checkout"
        >
          <span v-if="isSubmitting"><i class="fas fa-circle-notch fa-spin"></i> Memproses pembayaran...</span>
          <span v-else-if="!selectedMethodCode">Pilih metode pembayaran</span>
          <span v-else>Bayar {{ formatCurrency(totalAmount) }}</span>
          <i v-if="!isSubmitting && selectedMethodCode" class="fas fa-arrow-right"></i>
        </button>

        <button type="button" class="checkout-back-button" :disabled="isSubmitting" @click="backToPackage">
          <i class="fas fa-arrow-left"></i> Kembali pilih paket
        </button>

        <p class="checkout-gateway-note">
          <i class="fas fa-shield-alt"></i>
          Pembayaran diproses aman melalui {{ gatewayLabel }}.
        </p>
      </aside>
    </div>
  </main>
</template>

<script>
import clinicModule from "@/core/modules/CrudModule.js";
import paymentGateway from "@/core/services/payment-gateway.service.js";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";

export default {
  data() {
    return {
      methods: [],
      payment: {},
      selectedMethodCode: "",
      isLoading: true,
      isSubmitting: false,
      loadError: "",
      submitError: "",
    };
  },

  computed: {
    packageName() {
      return (this.payment.name || "Paket Medhop").toUpperCase();
    },
    duration() {
      return this.numberValue(this.payment.duration);
    },
    unitPrice() {
      return this.numberValue(this.payment.price);
    },
    subtotal() {
      return this.numberValue(this.payment.after_price) || this.unitPrice * this.duration;
    },
    taxAmount() {
      return this.numberValue(this.payment.tax_amount);
    },
    totalAmount() {
      return this.numberValue(this.payment.total_amount) || this.subtotal + this.taxAmount;
    },
    canCheckout() {
      return Boolean(this.selectedMethodCode && !this.isSubmitting && this.totalAmount > 0);
    },
    gatewayLabel() {
      return paymentGateway.label;
    },
  },

  methods: {
    numberValue(value) {
      const number = Number(value);
      return Number.isFinite(number) ? number : 0;
    },
    formatCurrency(value) {
      return new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: "IDR",
        maximumFractionDigits: 0,
      }).format(this.numberValue(value));
    },
    categoryLabel(category) {
      const labels = { 1: "Transfer bank", 2: "Gerai retail", 3: "Dompet digital" };
      return labels[Number(category)] || "Pembayaran digital";
    },
    async getMethods() {
      this.isLoading = true;
      this.loadError = "";
      try {
        const response = await clinicModule.get("subscription/payment-methods");
        this.methods = response && Array.isArray(response.items) ? response.items : [];
      } catch (error) {
        this.methods = [];
        this.loadError = "Periksa koneksi Anda, lalu muat ulang metode pembayaran.";
      } finally {
        this.isLoading = false;
      }
    },
    setPaymentMethod(method) {
      this.selectedMethodCode = method.code;
      this.submitError = "";
      this.$set(this.payment, "payment_method_name", method.payment_method_name);
      this.$set(this.payment, "payment_method_category", Number(method.category));
      this.$set(this.payment, "payment_method_code", method.code);
      this.$set(this.payment, "url_image", method.url_image);
    },
    async checkout() {
      if (!this.canCheckout) return;
      this.isSubmitting = true;
      this.submitError = "";

      try {
        const response = await paymentGateway.createInvoice(this.payment);
        const invoice = paymentGateway.normalizeInvoice(response, this.payment);
        if (!invoice) throw new Error("Invoice tidak tersedia.");

        localStorage.setItem("payment", JSON.stringify(invoice));
        this.$router.push("/subscribe/payment");
      } catch (error) {
        this.submitError =
          error && error.message
            ? error.message
            : "Pembayaran belum dapat diproses. Silakan coba kembali.";
      } finally {
        this.isSubmitting = false;
      }
    },
    backToPackage() {
      this.$router.push("/subscribe/package");
    },
  },

  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Berlangganan", route: "" },
      { title: "Checkout" },
    ]);

    try {
      const paymentData = JSON.parse(localStorage.getItem("paymentDetailData"));
      if (!paymentData || !paymentData.id) {
        this.$router.push("/subscribe/package");
        return;
      }
      this.payment = paymentData;
      this.getMethods();
    } catch (error) {
      this.$router.push("/subscribe/package");
    }
  },
};
</script>

<style scoped>
.payment-checkout-page {
  --navy: var(--skin-deep);
  --blue: var(--skin-primary);
  --teal: var(--skin-secondary);
  --ink: #17243b;
  --muted: #6b7280;
  --border: #e3eaf2;
  max-width: 1360px;
  margin: 0 auto;
  padding: 0 0 38px;
  color: var(--ink);
}

/* The legacy theme sets a fixed gray on Font Awesome icons.
   Keep checkout icons aligned with the color of their Medhop component. */
.payment-checkout-page i {
  color: currentColor !important;
}

.checkout-hero {
  position: relative;
  z-index: 0;
  min-height: 152px;
  padding: 30px 34px;
  overflow: hidden;
  border-radius: 20px;
  background: linear-gradient(118deg, #112c5c 0%, var(--skin-primary) 57%, #1b918c 100%);
  box-shadow: 0 16px 35px rgba(var(--skin-deep-rgb), 0.17);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

.checkout-hero::after {
  content: "";
  position: absolute;
  inset: 0;
  z-index: -1;
  opacity: 0.16;
  background-image: radial-gradient(circle at 1px 1px, #fff 1px, transparent 1.5px);
  background-size: 22px 22px;
  mask-image: linear-gradient(90deg, transparent 30%, #000);
}

.checkout-hero__orb { position: absolute; z-index: -1; border-radius: 50%; border: 1px solid rgba(255,255,255,.22); }
.checkout-hero__orb--one { width: 170px; height: 170px; right: 13%; top: -85px; background: rgba(105, 224, 206, .13); }
.checkout-hero__orb--two { width: 110px; height: 110px; right: -24px; bottom: -52px; background: rgba(255,255,255,.1); }
.checkout-hero__content, .checkout-security, .checkout-step, .checkout-section-heading, .checkout-summary__header, .checkout-package, .checkout-selected-method { display: flex; align-items: center; }
.checkout-hero__content { gap: 18px; }
.checkout-hero__icon { width: 58px; height: 58px; flex: 0 0 58px; border: 1px solid rgba(255,255,255,.24); border-radius: 16px; background: rgba(255,255,255,.14); display: grid; place-items: center; font-size: 23px; backdrop-filter: blur(8px); }
.checkout-hero h1 { margin: 3px 0 5px; color: #fff !important; font-size: 25px; font-weight: 750; letter-spacing: -.35px; }
.checkout-hero p { margin: 0; color: rgba(255,255,255,.8); font-size: 13px; }
.checkout-eyebrow { color: var(--teal); font-size: 10px; font-weight: 800; letter-spacing: .12em; text-transform: uppercase; }
.checkout-eyebrow--light { color: #8ee4d7; }
.checkout-security { gap: 12px; min-width: 260px; padding: 12px 15px; border: 1px solid rgba(255,255,255,.2); border-radius: 14px; background: rgba(9,34,69,.2); backdrop-filter: blur(8px); }
.checkout-security > span { width: 34px; height: 34px; border-radius: 10px; background: rgba(255,255,255,.14); display: grid; place-items: center; color: #9af0df; }
.checkout-security strong, .checkout-security small { display: block; }
.checkout-security strong { color: #fff; font-size: 12px; }
.checkout-security small { margin-top: 2px; color: #d9f7f3 !important; font-size: 10px; font-weight: 500; }

.checkout-steps { margin: 18px 0; padding: 14px 22px; border: 1px solid var(--border); border-radius: 16px; background: #fff; display: flex; align-items: center; justify-content: center; gap: clamp(12px, 4vw, 54px); box-shadow: 0 8px 24px rgba(var(--skin-deep-rgb),.05); }
.checkout-step { gap: 10px; color: var(--blue); }
.checkout-step > span { width: 30px; height: 30px; flex: 0 0 30px; border: 1px solid #c8dce9; border-radius: 50%; background: #edf6fa; color: var(--blue); display: grid; place-items: center; font-size: 11px; font-weight: 800; }
.checkout-step small, .checkout-step strong { display: block; }
.checkout-step small { font-size: 9px; text-transform: uppercase; letter-spacing: .08em; }
.checkout-step strong { font-size: 11px; }
.checkout-step--complete { color: var(--teal); }
.checkout-step--complete > span { border-color: #c9ebe7; background: var(--skin-soft); }
.checkout-step--active { color: var(--navy); }
.checkout-step--active > span { border-color: var(--navy); background: var(--navy); color: #fff; box-shadow: 0 4px 10px rgba(var(--skin-deep-rgb),.24); }
.checkout-steps__arrow { color: var(--teal); font-size: 9px; opacity: .72; }

.checkout-layout { display: grid; grid-template-columns: minmax(0, 1.65fr) minmax(330px, .75fr); gap: 20px; align-items: start; }
.checkout-methods-card, .checkout-summary { border: 1px solid var(--border); border-radius: 18px; background: #fff; box-shadow: 0 10px 30px rgba(var(--skin-deep-rgb),.06); }
.checkout-methods-card { padding: 25px; }
.checkout-summary { position: sticky; top: 92px; padding: 23px; }
.checkout-section-heading, .checkout-summary__header { justify-content: space-between; gap: 15px; }
.checkout-section-heading { margin-bottom: 22px; }
.checkout-section-heading h2, .checkout-summary h2 { margin: 4px 0; color: var(--navy); font-size: 18px; font-weight: 750; letter-spacing: -.2px; }
.checkout-section-heading p { margin: 0; color: var(--muted); font-size: 11px; }
.checkout-count { padding: 6px 10px; border-radius: 999px; background: var(--skin-soft); color: var(--teal); font-size: 10px; font-weight: 800; }

.checkout-method-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 13px; }
.payment-method { position: relative; min-height: 142px; padding: 17px 14px 14px; border: 1.5px solid var(--border); border-radius: 15px; outline: none; background: #fff; color: inherit; text-align: left; transition: border-color .2s, box-shadow .2s, transform .2s, background .2s; }
.payment-method:hover { border-color: #70b7b2; background: #f8fcfc; box-shadow: 0 8px 18px rgba(var(--skin-primary-rgb),.09); transform: translateY(-2px); }
.payment-method:focus-visible { box-shadow: 0 0 0 3px rgba(var(--skin-secondary-rgb),.18); border-color: var(--teal); }
.payment-method--selected { border-color: var(--teal); background: linear-gradient(145deg, #f4fbfa, #fff); box-shadow: 0 9px 20px rgba(var(--skin-secondary-rgb),.12); }
.payment-method__check { position: absolute; top: 9px; right: 9px; width: 22px; height: 22px; border-radius: 50%; background: var(--teal); color: #fff; display: grid; place-items: center; font-size: 9px; }
.payment-method__logo { width: 100%; height: 58px; padding: 7px 20px; border-radius: 10px; background: #f8fafc; display: flex; align-items: center; justify-content: center; }
.payment-method__logo img { max-width: 100%; max-height: 44px; object-fit: contain; }
.payment-method__content { display: block; margin-top: 12px; text-align: center; }
.payment-method__content strong, .payment-method__content small { display: block; }
.payment-method__content strong { overflow: hidden; color: var(--navy); font-size: 11px; text-overflow: ellipsis; white-space: nowrap; }
.payment-method__content small { margin-top: 3px; color: var(--muted); font-size: 9px; }

.method-skeleton { height: 142px; padding: 16px; border: 1px solid #edf1f5; border-radius: 15px; }
.method-skeleton span, .method-skeleton i { display: block; border-radius: 7px; background: linear-gradient(90deg,#f1f4f7 25%,#f8fafb 50%,#f1f4f7 75%); background-size: 200% 100%; animation: checkout-shimmer 1.4s infinite; }
.method-skeleton span { height: 58px; }
.method-skeleton i { width: 70%; height: 9px; margin: 13px auto 0; }
.method-skeleton i:last-child { width: 45%; margin-top: 7px; }
@keyframes checkout-shimmer { to { background-position: -200% 0; } }

.checkout-state { min-height: 210px; padding: 30px; border: 1px dashed #dbe5ee; border-radius: 15px; background: #fbfcfd; text-align: center; display: flex; flex-direction: column; align-items: center; justify-content: center; }
.checkout-state > span { width: 46px; height: 46px; margin-bottom: 12px; border-radius: 13px; background: #edf6f7; color: var(--teal); display: grid; place-items: center; font-size: 18px; }
.checkout-state strong { color: var(--navy); font-size: 13px; }
.checkout-state p { margin: 4px 0 0; color: var(--muted); font-size: 10px; }
.checkout-state button { margin-top: 13px; padding: 8px 14px; border: 0; border-radius: 9px; background: var(--navy); color: #fff; font-size: 10px; font-weight: 700; }
.checkout-state--error > span { background: #fff0ee; color: #d95245; }

.checkout-assurance { margin-top: 22px; padding-top: 18px; border-top: 1px solid #edf1f5; display: grid; grid-template-columns: repeat(2, 1fr); gap: 18px; }
.checkout-assurance > div { display: flex; align-items: center; gap: 10px; }
.checkout-assurance > div > span { width: 35px; height: 35px; flex: 0 0 35px; border-radius: 10px; background: var(--skin-soft); color: var(--teal); display: grid; place-items: center; font-size: 12px; }
.checkout-assurance p { margin: 0; }
.checkout-assurance strong, .checkout-assurance small { display: block; }
.checkout-assurance strong { color: var(--navy); font-size: 10px; }
.checkout-assurance small { margin-top: 2px; color: var(--muted); font-size: 9px; line-height: 1.35; }

.checkout-summary__receipt { width: 40px; height: 40px; border-radius: 12px; background: #edf6f7; color: var(--teal); display: grid; place-items: center; }
.checkout-package { gap: 12px; margin: 19px 0; padding: 14px; border-radius: 13px; background: linear-gradient(135deg, #f3f7fb, #f7fbfa); }
.checkout-package__icon { width: 39px; height: 39px; flex: 0 0 39px; border-radius: 11px; background: #fff; color: var(--navy); display: grid; place-items: center; box-shadow: 0 5px 12px rgba(var(--skin-deep-rgb),.08); }
.checkout-package small, .checkout-package strong, .checkout-package div > span { display: block; }
.checkout-package small { color: var(--muted); font-size: 9px; }
.checkout-package strong { margin: 2px 0; color: var(--navy); font-size: 11px; }
.checkout-package div > span { color: var(--teal); font-size: 9px; font-weight: 700; }
.checkout-breakdown { margin: 0; padding: 0 2px 14px; border-bottom: 1px solid var(--border); }
.checkout-breakdown > div { margin-top: 10px; display: flex; justify-content: space-between; gap: 12px; }
.checkout-breakdown dt, .checkout-breakdown dd { margin: 0; font-size: 10px; }
.checkout-breakdown dt { color: var(--muted); font-weight: 500; }
.checkout-breakdown dd { color: var(--ink); font-weight: 700; text-align: right; }
.checkout-free { color: var(--teal) !important; }
.checkout-total { margin: 14px 0; padding: 14px 15px; border-radius: 13px; background: var(--navy); color: #fff; }
.checkout-total span, .checkout-total strong, .checkout-total small { display: block; }
.checkout-total span { color: #b8c9df; font-size: 9px; }
.checkout-total strong { margin: 3px 0; color: #fff; font-size: 21px; letter-spacing: -.3px; }
.checkout-total small { color: #8ee4d7; font-size: 9px; }
.checkout-selected-method { gap: 10px; padding: 12px; border: 1px solid #cce8e5; border-radius: 12px; background: #f5fbfa; }
.checkout-selected-method--empty { border-style: dashed; border-color: #dce4ed; background: #fbfcfd; }
.checkout-selected-method__logo { width: 42px; height: 32px; flex: 0 0 42px; padding: 4px; border-radius: 8px; background: #e9f6f5; display: grid; place-items: center; color: var(--teal); }
.checkout-selected-method__logo img { max-width: 100%; max-height: 24px; object-fit: contain; }
.checkout-selected-method div { flex: 1; }
.checkout-selected-method small, .checkout-selected-method strong { display: block; }
.checkout-selected-method small { color: var(--muted); font-size: 8px; }
.checkout-selected-method strong { margin-top: 2px; color: var(--navy); font-size: 10px; }
.checkout-selected-method > i { color: var(--teal); }
.checkout-pay-button { width: 100%; min-height: 47px; margin-top: 13px; padding: 12px 15px; border: 0; border-radius: 12px; background: linear-gradient(110deg, var(--navy), var(--blue) 65%, var(--teal)); color: #fff; font-size: 11px; font-weight: 750; display: flex; align-items: center; justify-content: center; gap: 10px; box-shadow: 0 9px 18px rgba(var(--skin-deep-rgb),.19); transition: transform .2s, box-shadow .2s, opacity .2s; }
.checkout-pay-button:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 12px 22px rgba(var(--skin-deep-rgb),.25); }
.checkout-pay-button:disabled { background: #dce3eb; color: #8c98a8; cursor: not-allowed; box-shadow: none; }
.checkout-back-button { width: 100%; min-height: 38px; margin-top: 5px; padding: 9px; border: 0; background: transparent; color: var(--blue); font-size: 10px; font-weight: 700; line-height: 1; display: inline-flex; align-items: center; justify-content: center; gap: 7px; }
.checkout-back-button i { flex: 0 0 auto; color: var(--blue) !important; font-size: 13px; line-height: 1; transform: translateY(-.25px); }
.checkout-gateway-note { margin: 5px 0 0; color: var(--muted); font-size: 8.5px; line-height: 1.35; text-align: center; display: flex; align-items: center; justify-content: center; gap: 6px; }
.checkout-gateway-note i { flex: 0 0 auto; margin: 0; color: var(--teal) !important; font-size: 12px; line-height: 1; }
.checkout-submit-error { margin: 10px 0 0; padding: 9px 10px; border-radius: 9px; background: #fff1ef; color: #ba4339; font-size: 9px; line-height: 1.4; }

@media (max-width: 991px) {
  .checkout-layout { grid-template-columns: 1fr; }
  .checkout-summary { position: static; }
  .checkout-method-grid { grid-template-columns: repeat(3, 1fr); }
}

@media (max-width: 767px) {
  .checkout-hero { padding: 24px; align-items: flex-start; flex-direction: column; }
  .checkout-security { width: 100%; min-width: 0; }
  .checkout-steps { gap: 9px; justify-content: space-between; }
  .checkout-step div { display: none; }
  .checkout-methods-card, .checkout-summary { padding: 18px; border-radius: 15px; }
  .checkout-method-grid { grid-template-columns: repeat(2, 1fr); }
  .checkout-assurance { grid-template-columns: 1fr; }
}

@media (max-width: 390px) {
  .checkout-method-grid { grid-template-columns: 1fr; }
  .payment-method { min-height: 124px; }
}
</style>
